// src/components/announcements/CreateAnnouncement.js
import React, { useState } from 'react';
import { supabase } from '../../config/supabase.config';

function CreateAnnouncement({ userData, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general',
    priority: 'normal',
    target_audience: 'all',
    is_pinned: false,
    scheduled_date: '',
    expiry_date: ''
  });
  const [loading, setLoading] = useState(false);
  
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      alert('Please fill in title and content');
      return;
    }

    if (!isAdmin) {
      alert('Only administrators can create announcements');
      return;
    }

    setLoading(true);
    try {
      console.log('Creating announcement using database function...');

      // Use the database function to bypass RLS
      const { data, error } = await supabase.rpc('create_announcement_admin', {
        p_title: formData.title,
        p_content: formData.content,
        p_category: formData.category,
        p_priority: formData.priority,
        p_target_audience: formData.target_audience,
        p_is_pinned: formData.is_pinned,
        p_author_id: userData.id,
        p_scheduled_date: formData.scheduled_date || null,
        p_expiry_date: formData.expiry_date || null
      });

      if (error) {
        console.error('Database function error:', error);
        throw error;
      }
      
      console.log('Announcement created successfully:', data);
      
      setFormData({
        title: '',
        content: '',
        category: 'general',
        priority: 'normal',
        target_audience: 'all',
        is_pinned: false,
        scheduled_date: '',
        expiry_date: ''
      });
      
      alert('Announcement created successfully!');
      if (onSuccess) onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Error creating announcement:', error);
      
      if (error.message?.includes('Only admins')) {
        alert('Only administrators can create announcements.');
      } else if (error.code === '42883') {
        alert('Database function not found. Please create the create_announcement_admin function in Supabase.');
      } else {
        alert(`Failed to create announcement: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Rest of the component remains the same...
  if (!isAdmin) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '30px',
          textAlign: 'center'
        }}>
          <h3>Access Denied</h3>
          <p>Only administrators can create announcements.</p>
          <button
            onClick={onClose}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '30px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Form content remains exactly the same */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0 }}>📢 Create Announcement</h3>
          <span style={{
            padding: '5px 10px',
            background: '#e7f3ff',
            color: '#0066cc',
            borderRadius: '5px',
            fontSize: '12px'
          }}>
            Admin Mode
          </span>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* All form fields remain the same as before */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                fontSize: '14px'
              }}
              placeholder="Enter announcement title"
              maxLength="255"
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
              Content *
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                minHeight: '120px',
                resize: 'vertical',
                fontSize: '14px'
              }}
              placeholder="Enter announcement content..."
              required
            />
          </div>

          {/* Rest of the form fields... */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '15px', 
            marginBottom: '15px' 
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}
              >
                <option value="general">📌 General</option>
                <option value="maintenance">🔧 Maintenance</option>
                <option value="event">🎉 Event</option>
                <option value="emergency">🚨 Emergency</option>
                <option value="financial">💰 Financial</option>
                <option value="rules">📋 Rules & Regulations</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}
              >
                <option value="normal">🟢 Normal</option>
                <option value="important">🟡 Important</option>
                <option value="urgent">🔴 Urgent</option>
                <option value="info">🔵 Info</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
              Target Audience
            </label>
            <select
              value={formData.target_audience}
              onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Residents</option>
              <option value="owners">Owners Only</option>
              <option value="tenants">Tenants Only</option>
            </select>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '15px', 
            marginBottom: '15px' 
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                Schedule Date (Optional)
              </label>
              <input
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}
                min={new Date().toISOString().split('T')[0]}
              />
              <small style={{ color: '#666', fontSize: '11px' }}>
                Announcement will be visible from this date
              </small>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                Expiry Date (Optional)
              </label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}
                min={formData.scheduled_date || new Date().toISOString().split('T')[0]}
              />
              <small style={{ color: '#666', fontSize: '11px' }}>
                Leave empty for permanent announcement
              </small>
            </div>
          </div>

          <div style={{ 
            marginBottom: '20px',
            padding: '10px',
            background: '#f8f9fa',
            borderRadius: '5px'
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              cursor: 'pointer' 
            }}>
              <input
                type="checkbox"
                checked={formData.is_pinned}
                onChange={(e) => setFormData({...formData, is_pinned: e.target.checked})}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '600' }}>
                📌 Pin this announcement to top
              </span>
            </label>
            <small style={{ 
              display: 'block', 
              marginTop: '5px', 
              marginLeft: '28px',
              color: '#666' 
            }}>
              Pinned announcements always appear first
            </small>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                background: loading ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              {loading ? 'Creating...' : 'Create Announcement'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateAnnouncement;